import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdRefresh, MdSave } from 'react-icons/md';
import AppLayout from '../components/AppLayout';
import Header from '../components/header';
import { getData, putData } from '../utils/serverHelper';
import { errorMessage, successMessage } from '../logic/error/action';

const API = '/admin/industry-settings';

const MODULE_HELP = {
  appointments: 'Booking and calendar',
  customers: 'Customer / patient / client records',
  staff: 'Team members who take appointments',
  services: 'What the business offers',
  products: 'Inventory and POS items',
  billing: 'Invoices and payments',
  reports: 'Business reports',
  documents: 'Files and prescriptions',
  memberships: 'Memberships / packages',
  jobs: 'Job cards / work orders',
};

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

const IndustrySettings = () => {
  const dispatch = useDispatch();
  const [industries, setIndustries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async (keepSelection = true) => {
    setLoading(true);
    try {
      const list = unwrap(await getData(API)) || [];
      setIndustries(Array.isArray(list) ? list : []);
      const keep = keepSelection && list.find((i) => i.preset_id === selectedId);
      const pick = keep || list[0];
      setSelectedId(pick ? pick.preset_id : null);
      setDraft(pick ? pick.modules.map((m) => ({ ...m })) : []);
    } catch (error) {
      errorMessage(dispatch, error?.response?.data?.error?.message || 'Could not load industry settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(() => industries.find((i) => i.preset_id === selectedId), [industries, selectedId]);

  const dirty = useMemo(() => {
    if (!selected) return false;
    return JSON.stringify(selected.modules) !== JSON.stringify(draft);
  }, [selected, draft]);

  const choose = (industry) => {
    if (dirty && !window.confirm('Discard unsaved changes for this industry?')) return;
    setSelectedId(industry.preset_id);
    setDraft(industry.modules.map((m) => ({ ...m })));
  };

  const update = (key, patch) => setDraft((prev) => prev.map((m) => (m.key === key ? { ...m, ...patch } : m)));

  const save = async () => {
    if (!selected) return;
    const missing = draft.find((m) => m.enabled && (!m.singular.trim() || !m.plural.trim()));
    if (missing) {
      errorMessage(dispatch, `Give "${missing.key}" both a singular and a plural name.`);
      return;
    }
    setSaving(true);
    try {
      const res = unwrap(await putData(`${API}/${selected.preset_id}`, { modules: draft }));
      successMessage(
        dispatch,
        res?.created_new_industry
          ? `${selected.business_type} now has its own module settings. Saved.`
          : `Module settings saved for ${selected.business_type}.`
      );
      await load(true);
    } catch (error) {
      errorMessage(dispatch, error?.response?.data?.error?.message || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="w-full h-full flex flex-col items-start gap-4 p-4">
        <Header
          title="Module Settings"
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button startIcon={<MdRefresh />} onClick={() => load(true)} disabled={loading || saving} sx={{ textTransform: 'none' }}>
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<MdSave />}
                onClick={save}
                disabled={!dirty || saving}
                sx={{ textTransform: 'none' }}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
            </Box>
          }
        />

        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          Choose which modules each industry type has and what they're called. Businesses in that industry see the
          changes in the web and mobile apps within about a minute.
        </Typography>

        {loading ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : industries.length === 0 ? (
          <Alert severity="info" sx={{ mx: 2 }}>
            No industry types yet. Add one under Industries first.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, width: '100%', px: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Paper variant="outlined" sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, maxHeight: '70vh', overflow: 'auto' }}>
              <List dense disablePadding>
                {industries.map((ind) => (
                  <ListItemButton
                    key={ind.preset_id}
                    selected={ind.preset_id === selectedId}
                    onClick={() => choose(ind)}
                  >
                    <ListItemText
                      primary={ind.business_type}
                      secondary={`${ind.business_count} business${ind.business_count === 1 ? '' : 'es'}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            {selected && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selected.business_type}
                  </Typography>
                  {!selected.has_own_config && (
                    <Tooltip title="This type currently uses the default (Clinic) settings. Saving gives it its own copy.">
                      <Chip size="small" color="warning" label="Using default settings" />
                    </Tooltip>
                  )}
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">On</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name (one)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name (many) — shown in menus</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {draft.map((m) => (
                        <TableRow key={m.key} sx={{ opacity: m.enabled ? 1 : 0.55 }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {m.key}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {MODULE_HELP[m.key] || ''}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={m.required ? 'Always on for every industry' : ''}>
                              <span>
                                <Switch
                                  checked={m.enabled}
                                  disabled={m.required}
                                  onChange={(e) => update(m.key, { enabled: e.target.checked })}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={m.singular}
                              onChange={(e) => update(m.key, { singular: e.target.value })}
                              placeholder="e.g. Client"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={m.plural}
                              onChange={(e) => update(m.key, { plural: e.target.value })}
                              placeholder="e.g. Clients"
                              fullWidth
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </div>
    </AppLayout>
  );
};

export default IndustrySettings;
